export const ADMIN_PLACEHOLDER = "Not provided";

function clean(value) {
  if (value === null || value === undefined) return "";
  const str = String(value).replace(/\s+/g, " ").trim();
  return str;
}

export function coalesceText(...values) {
  for (const value of values) {
    const normalized = clean(value);
    if (normalized) return normalized;
  }
  return "";
}

export function withPlaceholder(value, fallback = ADMIN_PLACEHOLDER) {
  const normalized = clean(value);
  return normalized || fallback;
}

export function formatAdminDate(value, fallback = ADMIN_PLACEHOLDER) {
  if (!value) return fallback;
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
  } catch {
    return String(value);
  }
}

export function extractCustomerId(source) {
  return coalesceText(
    source?.customer_profile?.customer_id,
    source?.customer_identity?.customer_id,
    source?.id,
    source?.customer_id,
    source?.customerId,
    source?.customer?.id,
    source?.customer?.customer_id,
    source?.customer?.customerId,
    source?.customer_profile?.id
  );
}

export function extractCustomerName(source) {
  const direct = coalesceText(
    source?.customer_profile?.full_name,
    source?.customer_identity?.full_name,
    source?.full_name,
    source?.fullName,
    source?.customer_name,
    source?.name,
    source?.customer?.full_name
  );
  if (direct) return direct;

  const first = coalesceText(
    source?.customer_profile?.first_name,
    source?.customer_identity?.first_name,
    source?.first_name,
    source?.firstName
  );
  const last = coalesceText(
    source?.customer_profile?.last_name,
    source?.customer_identity?.last_name,
    source?.last_name,
    source?.lastName
  );
  const combined = [first, last].filter(Boolean).join(" ").trim();
  if (combined) return combined;

  return "";
}

export function extractCustomerEmail(source) {
  return coalesceText(
    source?.customer_profile?.email,
    source?.customer_identity?.email,
    source?.email,
    source?.customer_email,
    source?.customerEmail,
    source?.customer?.email
  );
}

export function extractCustomerPhone(source) {
  return coalesceText(
    source?.customer_profile?.phone,
    source?.customer_identity?.phone,
    source?.phone,
    source?.mobile,
    source?.telephone,
    source?.customer?.phone
  );
}

export function extractCustomerAddress(source) {
  return coalesceText(
    source?.customer_profile?.address,
    source?.customer_identity?.address,
    source?.address,
    source?.street,
    source?.delivery_address,
    source?.deliveryAddress,
    source?.customer?.address
  );
}

export function extractCustomerCity(source) {
  return coalesceText(
    source?.customer_profile?.city,
    source?.customer_identity?.city,
    source?.city,
    source?.town,
    source?.customer?.city
  );
}

export function extractCustomerCountry(source) {
  return coalesceText(
    source?.customer_profile?.country,
    source?.customer_identity?.country,
    source?.country,
    source?.country_name,
    source?.countryCode,
    source?.customer?.country
  );
}

export function extractDeliveryNotes(source) {
  return coalesceText(
    source?.customer_profile?.delivery_notes,
    source?.customer_identity?.delivery_notes,
    source?.delivery_notes,
    source?.deliveryNotes,
    source?.notes,
    source?.customer_notes,
    source?.customerNotes,
    source?.customer?.delivery_notes,
    source?.customer?.notes
  );
}

export function extractCustomerLanguage(source) {
  return coalesceText(
    source?.customer_profile?.lang,
    source?.customer_identity?.lang,
    source?.lang,
    source?.language,
    source?.preferred_language,
    source?.customer?.lang
  );
}

export function extractVerification(source) {
  if (typeof source?.customer_profile?.email_verified === "boolean") return source.customer_profile.email_verified;
  if (typeof source?.customer_identity?.email_verified === "boolean") return source.customer_identity.email_verified;
  if (typeof source?.email_verified === "boolean") return source.email_verified;
  if (typeof source?.verified === "boolean") return source.verified;
  if (typeof source?.is_verified === "boolean") return source.is_verified;
  if (typeof source?.emailVerified === "boolean") return source.emailVerified;
  if (typeof source?.customer?.email_verified === "boolean") return source.customer.email_verified;
  return null;
}

export function extractCustomerIdentity(source) {
  const customerId = extractCustomerId(source);
  const fullName = extractCustomerName(source);
  const email = extractCustomerEmail(source);
  const phone = extractCustomerPhone(source);
  const address = extractCustomerAddress(source);
  const city = extractCustomerCity(source);
  const country = extractCustomerCountry(source);
  const deliveryNotes = extractDeliveryNotes(source);
  const language = extractCustomerLanguage(source);
  const verification = extractVerification(source);

  return {
    customerId,
    fullName,
    email,
    phone,
    address,
    city,
    country,
    deliveryNotes,
    language,
    verification,
    displayName: fullName || email || phone || customerId || "",
  };
}

export function extractOrderCustomer(order) {
  return extractCustomerIdentity({
    ...order,
    ...(order?.customer_profile || {}),
    ...(order?.customer_identity || {}),
    ...(order?.customer || {}),
    customer_profile: order?.customer_profile,
    customer_identity: order?.customer_identity,
    customer_id: order?.customer?.customer_id || order?.customer_id,
    customerId: order?.customer?.customerId || order?.customerId,
    customer_name: order?.customer?.customer_name || order?.customer_name,
    full_name: order?.customer?.full_name || order?.full_name,
    email: order?.customer?.email || order?.email,
    phone: order?.customer?.phone || order?.phone,
    address: order?.customer?.address || order?.address,
    city: order?.customer?.city || order?.city,
    country: order?.customer?.country || order?.country,
    delivery_notes: order?.customer?.delivery_notes || order?.delivery_notes,
  });
}

export function verificationLabel(source) {
  const value = typeof source === "boolean" ? source : extractVerification(source);
  if (value === true) return "Verified";
  if (value === false) return "Unverified";
  return "Not available";
}

export function formatLanguageLabel(value, fallback = ADMIN_PLACEHOLDER) {
  const normalized = clean(value).toLowerCase();
  if (!normalized) return fallback;
  const labels = {
    en: "EN",
    ar: "AR",
    fr: "FR",
  };
  return labels[normalized] || normalized.toUpperCase();
}

export async function readJsonSafe(response) {
  const text = await response.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function extractApiMessage(response, payload, fallback) {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (detail && typeof detail.message === "string" && detail.message.trim()) return detail.message.trim();
  if (typeof payload?.message === "string" && payload.message.trim()) return payload.message.trim();
  if (typeof payload?.error === "string" && payload.error.trim()) return payload.error.trim();
  return fallback || `Request failed (${response?.status || "unknown"})`;
}
